import { Box, Card, Typography } from '@mui/material';
import { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import ChatIcon from 'remixicon-react/QuestionAnswerFillIcon';
import SearchIcon from 'remixicon-react/Search2FillIcon';
import { useSearch } from '../../../Utilities/Search';
import { useMessageSender } from '../../../Utilities/SendMessage';
import { ConfigContext } from '../../App/App';
import { AppContext } from '../../App/AppContext';
import { useConversationalForms } from '../../ConversationalForms/FormsContext';
import { useLiveInlineField } from '../../ConversationalForms/useLiveInlineField';
import { ChatMessage } from '../../Shared/API/ApiTypes';
import ActionButton, { ActionButtonProps } from '../../Shared/ActionButton/ActionButton';

function SplashActions() {
  const config = useContext(ConfigContext);
  const { state } = useContext(AppContext);
  const navigate = useNavigate();
  const sendMessage = useMessageSender();
  const search = useSearch();
  // Same routing rule as the chat composer (MessageSender): when conversational
  // forms are active, every user-visible entry point sends through the forms
  // turn — open-field list, form_objectid, receipts, offers, and the turn's own
  // failure classification (store tracker 6.22: the home input used to bypass
  // it, so a visitor's first message got no forms behavior at all). The
  // provider mounts above the Routes in App.tsx, so it is available here.
  const { active: formsActive, sendTurn } = useConversationalForms();
  // While an inline sensitive control is live the visitor continues only
  // through it (Save or Skip): opening Chat shows it, and nothing is sent.
  const liveInline = useLiveInlineField();

  // TODO: temporary backwards-compat shim — old configs ship HomeHeadline1 as "Hey, ".
  // Treat blank/empty OR a "Hey"-prefixed value as unset so they fall back to the default
  // greeting. Remove once legacy tenant configs are updated.
  const rawHeadline = (config.HomeHeadline1 || "").toString().trim();
  const useDefaultGreeting = rawHeadline === "" || /^hey\b/i.test(rawHeadline);
  const greeting = useDefaultGreeting ? "Nice to meet you" : rawHeadline;

  const onSendMessage = async (message: ChatMessage) => {
    navigate("/Chat")
    if (formsActive && liveInline) return;
    if (formsActive) {
      // sendTurn posts the user's bubble itself and queues FIFO behind any
      // in-flight turn; the message and its receipts/start card land in the
      // chat log the navigation above just opened.
      sendTurn(message.content)
    } else {
      // Non-forms tenants keep the legacy path, byte-for-byte.
      sendMessage(message)
    }
  }

  const onChatActionSubmit = (inputText: string) => {
    if (inputText !== "") {
      onSendMessage({ content: inputText, direction: 0, time: new Date(Date.now()) })
    }
  }

  const onSearchActionSubmit = async (inputText: string) => {
    if (inputText !== "") {
      await search(inputText);
    }
  }
  const chatActionProps: ActionButtonProps = {
    icon: <ChatIcon fontSize='large' />,
    primaryText: config.HomeAskAQuestionHeadline1,
    secondaryText: config.HomeAskAQuestionHeadline2,
    onActionEnter: onChatActionSubmit
  }
  const searchActionProps: ActionButtonProps = {
    icon: <SearchIcon fontSize='large' />,
    primaryText: config.HomeSearchHeadline1,
    secondaryText: config.HomeSearchHeadline2,
    onActionEnter: onSearchActionSubmit
  }
  return (
    <>
      <Card sx={{
        display: 'flex', flexDirection: 'column', width: '100%', padding: `${config.PaddingNumber}px`, py: 2
      }} >
        <Typography gutterBottom sx={{ fontSize: '1.1rem' }} component="div">
          {config.HomeHeadline2}
        </Typography>
        <Box sx={{ display: 'flex' }}>
          <Typography paragraph={false} sx={{ fontSize: '1.3rem' }}>{greeting}{useDefaultGreeting && state.firstName !== '' ? ', ' : ''}</Typography>
          {useDefaultGreeting && <Typography color="primary" sx={{ fontWeight: 'bold', fontSize: '1.3rem', ml: 1, mb: 1 }}>{state.firstName}</Typography>}
        </Box>

        <Typography variant="body1" color="text.secondary">
          {config.HomeHeadline3}
        </Typography>
      </ Card>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: config.Gap, width: '100%' }}>
        <ActionButton actionButtonProps={chatActionProps} />
        <ActionButton actionButtonProps={searchActionProps} />
      </Box>
    </>
  );
}

export default SplashActions
